// @flow
import { Box } from "@chakra-ui/layout"
import * as React from "react"
import {
  currentVisibleOverlayAtom,
  showBadgeOverlayAtom
} from "../../recoil/atoms/overlay"
import { useRecoilValue, useRecoilState } from "recoil"
import BannerOverlay from "../servicesPanel/banners/_bannerOverlay"
import ChatOverlay from "../servicesPanel/chat/_chatOverlay"
import BadgeOverlay from "../servicesPanel/badges/_badgeOverlay"
import { auctionParamsSelector } from "../../recoil/atoms/auction"
import { AuctionOverlay } from "../servicesPanel/auctions/auction-overlay"
import { ProductSelectorSizeOverlayType } from "../servicesPanel/badges/product-selector/types"
import { viewDependingLayoutAtom } from "../../recoil/atoms/view"
import { productOverlaySelector } from "../../recoil/atoms/product-overlay"
import { TimerOverlay } from "../common/timerOverlay"
import { showTimerOverlayAtom } from "../../recoil/atoms/timer-overlay"
import useWindowSize from "../../hooks/useWindowSize"
import { isMobileVideoTallerSelector } from "../../recoil/atoms/mainWindowResolution"
const AuctionStatus = window.MeetOnline.AuctionStatus

interface ScreenOverlayProps {
  isMobile?: boolean
  width?: number | string
  isSmallMainScreen?: boolean
  maxWidthOverlay?: number
  isBorder?: boolean
  isMediumAuctionBadge?: boolean
  isTouch?: boolean
  isTablet?: boolean
  productSelectorSizeOverlayProps: ProductSelectorSizeOverlayType
  stylesBadge?: {
    container: React.CSSProperties
  }
  maxWidthChatOverlay: number
}

const ScreenOverlay: React.FC<ScreenOverlayProps> = React.memo(
  ({
    isMobile,
    width,
    isSmallMainScreen,
    maxWidthOverlay,
    isBorder,
    isMediumAuctionBadge,
    isTouch,
    productSelectorSizeOverlayProps,
    isTablet,
    stylesBadge,
    maxWidthChatOverlay
  }) => {
    const _overlay_ = useRecoilValue(currentVisibleOverlayAtom)
    const [isOverlayBadge] = useRecoilState<boolean>(showBadgeOverlayAtom)
    const { status, isShowOverlay } = useRecoilValue(auctionParamsSelector)
    const { isTransparent, isLandscape } = useRecoilValue(
      viewDependingLayoutAtom
    )
    const { isOpen } = useRecoilValue(productOverlaySelector)
    const { screenSize } = useWindowSize()

    const isTimerOverlayActive = useRecoilValue(showTimerOverlayAtom)
    const isMobileVideoTaller = useRecoilValue(isMobileVideoTallerSelector)

    const renderOverlay = React.useCallback(() => {
      if (isTransparent || isLandscape) {
        if (isOpen) {
          return (
            <>
              {isOverlayBadge && (
                <BadgeOverlay
                  {..._overlay_}
                  width={maxWidthOverlay?.toString() ?? width}
                  maxWidthOverlay={maxWidthOverlay ?? width}
                  isTouch={isTouch}
                  isLandscape={isLandscape}
                  isSmallMainScreen={isSmallMainScreen}
                  isMobile={isMobile}
                  productSelectorSizeOverlayProps={
                    productSelectorSizeOverlayProps
                  }
                  isTablet={isTablet}
                  stylesBadge={stylesBadge}
                />
              )}
            </>
          )
        }
      }
      if (isTimerOverlayActive) {
        return (
          <TimerOverlay
            key={"timer-overlay"}
            maxWidth={maxWidthChatOverlay}
            isTablet={isTablet}
            isMobile={isMobile}
            isSmallMainScreen={isSmallMainScreen}
          />
        )
      } else if (status === AuctionStatus.IN_PROGRESS && isShowOverlay) {
        return (
          <AuctionOverlay
            id="auction-overlay"
            width={width}
            isTouch={isTouch}
            isSmallMainScreen={isSmallMainScreen}
            isMobile={isMobile}
            isLandscape={isLandscape}
            isMediumAuctionBadge={isMediumAuctionBadge}
          />
        )
      } else if (status === AuctionStatus.IN_PROGRESS && !isShowOverlay) {
        return null
      } else {
        switch (_overlay_?.type) {
          case "banner":
            return <BannerOverlay {..._overlay_} />
          case "chat":
            return (
              <>
                {isOverlayBadge && (
                  <ChatOverlay
                    {..._overlay_}
                    width={maxWidthChatOverlay}
                    isSmallMainScreen={isSmallMainScreen}
                    isMobile={isMobile}
                  />
                )}
              </>
            )
          case "badge":
            // show transparent badge when video resolution is Taller ie height is more then width
            if (
              // isTransparent && showTabsAtBottomOnMobile
              (isMobileVideoTaller || isTransparent)
              && isOverlayBadge && !isLandscape) {
              return (
                <BadgeOverlay
                  {..._overlay_}
                  stylesBadge={{
                    container: {
                      marginLeft: "0.5rem",
                      width: `${screenSize.width - 60}px`, // minus side button area
                      borderWidth: "2px"
                    }
                  }}
                  width={screenSize.width - 60}
                  isSmallMainScreen={false}
                  isMobile={true}
                  isTouch={true}
                  isRedirectToUrl={true}
                  isBadgeLiveMode={true}
                  isTransperent={false}
                  maxWidthOverlay={screenSize.width - 60 + ""}
                  showCloseButton={true}
                />
              )
            }

            return (
              <>
                {isOverlayBadge && (
                  <BadgeOverlay
                    {..._overlay_}
                    width={maxWidthOverlay?.toString() ?? width}
                    maxWidthOverlay={maxWidthOverlay ?? width}
                    isTouch={isTouch}
                    isLandscape={isLandscape}
                    isSmallMainScreen={isSmallMainScreen}
                    isMobile={isMobile}
                    productSelectorSizeOverlayProps={
                      productSelectorSizeOverlayProps
                    }
                    isTablet={isTablet}
                    stylesBadge={stylesBadge}
                  />
                )}
              </>
            )
          default:
            return null
        }
      }
    }, [
      _overlay_,
      isLandscape,
      isMediumAuctionBadge,
      isMobile,
      isOverlayBadge,
      isOpen,
      isShowOverlay,
      isSmallMainScreen,
      isTablet,
      isTimerOverlayActive,
      isTouch,
      isTransparent,
      maxWidthChatOverlay,
      maxWidthOverlay,
      productSelectorSizeOverlayProps,
      status,
      stylesBadge,
      width,
      screenSize.width,
      isMobileVideoTaller
    ])

    if (isMobile) {
      return renderOverlay()
    }
    return (
      <Box
        // maxWidth={maxWidthOverlay}
        // maxWidth={widthMainScreen}
        minHeight={"30px"}
        height="auto"
        bottom={"15px"}
        display="flex"
        flexDirection="row"
        zIndex="popover"
        p="0"
        m="0"
        left="0"
        marginLeft={isBorder ? "2px" : "0px"}
      >
        {renderOverlay()}
      </Box>
    )
  }
)
export default ScreenOverlay
